// SPDX-FileCopyrightText: 2025 Sequent Tech <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
// ====================================================================================
// 1. IMPORTS - All dependencies for the module in one place
// ====================================================================================
import { useState, useEffect, useCallback, createContext, useContext } from "react";
import initSqlJs from "sql.js";
import { openDB } from "idb";
import { useQuery } from "@apollo/client";
import { FETCH_DOCUMENT } from "@/queries/FetchDocument"; // Adjust path if needed
var IDB_NAME = "sqlite-databases";
var IDB_STORE_NAME = "database-cache";
// ====================================================================================
// 3. INTERNAL HOOKS - Helpers not exported from the module
// ====================================================================================
/**
 * Initializes the IndexedDB database.
 */
var initIdb = function () {
    return openDB(IDB_NAME, 1, {
        upgrade: function (db) {
            db.createObjectStore(IDB_STORE_NAME);
        },
    });
};
/**
 * An internal hook to download a database from a remote URL (via Hasura)
 * and cache it in IndexedDB. It is not exported.
 * @param electionEventId The Id of the election event to recover the database from.
 * @param documentId The document Id of the database file.
 */
function _useCachedDatabase(electionEventId, documentId, enabled) {
    var _this = this;
    var _a;
    if (enabled === void 0) { enabled = true; }
    // DO NOT return early. All hooks must be called on every render.
    var _b = useState(null), dbData = _b[0], setDbData = _b[1];
    var _c = useState(false), isLoading = _c[0], setIsLoading = _c[1];
    var _d = useState(null), error = _d[0], setError = _d[1];
    // ✅ The 'enabled' flag is passed to the 'skip' option.
    // This is the correct way to conditionally run a query.
    var _e = useQuery(FETCH_DOCUMENT, {
        variables: { electionEventId: electionEventId, documentId: documentId },
        skip: !enabled || !electionEventId || !documentId,
    }), isLoadingUrl = _e.loading, urlError = _e.error, documentData = _e.data;
    var remoteUrl = (_a = documentData === null || documentData === void 0 ? void 0 : documentData.fetchDocument) === null || _a === void 0 ? void 0 : _a.url;
    useEffect(function () {
        // ✅ The condition is now INSIDE the effect. This is allowed.
        // The useEffect hook itself is always called, but its logic only runs when enabled.
        if (!enabled || !documentId || !remoteUrl) {
            // If we are not enabled, ensure data is cleared for subsequent runs.
            if (!enabled)
                setDbData(null);
            return;
        }
        var loadDatabase = function () { return __awaiter(_this, void 0, void 0, function () {
            var db, cachedData, response, arrayBuffer, data, err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setError(null);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 9, 10, 11]);
                        return [4 /*yield*/, initIdb()];
                    case 2:
                        db = _a.sent();
                        return [4 /*yield*/, db.get(IDB_STORE_NAME, documentId)];
                    case 3:
                        cachedData = _a.sent();
                        if (!cachedData) return [3 /*break*/, 4];
                        setDbData(cachedData);
                        return [3 /*break*/, 8];
                    case 4:
                        setIsLoading(true);
                        return [4 /*yield*/, fetch(remoteUrl)];
                    case 5:
                        response = _a.sent();
                        if (!response.ok) {
                            throw new Error("Failed to fetch database from URL: ".concat(response.statusText, " (status: ").concat(response.status, ")"));
                        }
                        return [4 /*yield*/, response.arrayBuffer()];
                    case 6:
                        arrayBuffer = _a.sent();
                        data = new Uint8Array(arrayBuffer);
                        return [4 /*yield*/, db.put(IDB_STORE_NAME, data, documentId)];
                    case 7:
                        _a.sent();
                        setDbData(data);
                        _a.label = 8;
                    case 8: return [3 /*break*/, 11];
                    case 9:
                        err_1 = _a.sent();
                        console.error("[_useCachedDatabase] CRITICAL ERROR:", err_1);
                        setError(err_1.message);
                        return [3 /*break*/, 11];
                    case 10:
                        setIsLoading(false);
                        return [7 /*endfinally*/];
                    case 11: return [2 /*return*/];
                }
            });
        }); };
        loadDatabase();
        // Add `enabled` to the dependency array
    }, [enabled, documentId, remoteUrl]);
    return {
        dbData: dbData,
        // The isLoading/error states should only be active if the hook is enabled
        isLoading: enabled && (isLoadingUrl || isLoading),
        error: enabled ? (urlError ? urlError.message : error) : null,
    };
}
var DatabaseContext = createContext(null);
export function useDatabaseContext() {
    var context = useContext(DatabaseContext);
    if (!context)
        throw new Error("useDatabaseContext must be used within a DatabaseProvider");
    return context;
}
export function useDatabaseManager() {
    var _a = useState(new Map()), databases = _a[0], setDatabases = _a[1];
    var addDatabase = useCallback(function (name, database) {
        setDatabases(function (prev) { return new Map(prev).set(name, database); });
    }, []);
    var removeDatabase = useCallback(function (name) {
        setDatabases(function (prev) {
            var newMap = new Map(prev);
            newMap.delete(name);
            return newMap;
        });
    }, []);
    var contextValue = { databases: databases, addDatabase: addDatabase, removeDatabase: removeDatabase };
    return __assign(__assign({}, contextValue), { contextValue: contextValue });
}
export { DatabaseContext };
// ====================================================================================
// 5. PUBLIC HOOKS - The primary API for components
// ====================================================================================
/**
 * Manages loading a database and placing it into the global context.
 * This is the primary hook components should use to ensure a database is available.
 */
export function useManagedDatabase(documentId, electionEventId) {
    var _this = this;
    var _a = useDatabaseContext(), databases = _a.databases, addDatabase = _a.addDatabase;
    // 1. First, check if the fully initialized DB is already in our React context (memory).
    var isAlreadyInContext = documentId ? databases.has(documentId) : false;
    // 2. We only need to run the more expensive `_useCachedDatabase` hook if it's NOT in the context.
    var shouldLoadDatabase = !!documentId && !isAlreadyInContext;
    // 3. Call `_useCachedDatabase` and pass our conditional 'enabled' flag.
    //    If shouldLoadDatabase is false, this hook will do nothing and return immediately.
    var _b = _useCachedDatabase(electionEventId, documentId, shouldLoadDatabase), dbData = _b.dbData, isCacheLoading = _b.isLoading, cacheError = _b.error;
    var _c = useState(false), isInitializing = _c[0], setIsInitializing = _c[1];
    var _d = useState(null), initError = _d[0], setInitError = _d[1];
    // This useEffect for initializing the sql.js instance remains the same.
    // It will only run if `shouldLoadDatabase` was true and `dbData` was successfully retrieved.
    useEffect(function () {
        if (!dbData || !documentId || databases.has(documentId)) {
            return;
        }
        var initialize = function () { return __awaiter(_this, void 0, void 0, function () {
            var sql, db, e_1, errorMessage;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setIsInitializing(true);
                        setInitError(null);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, initSqlJs({
                                locateFile: function (file) { return "/".concat(file); },
                            })];
                    case 2:
                        sql = _a.sent();
                        db = new sql.Database(dbData);
                        addDatabase(documentId, db);
                        return [3 /*break*/, 5];
                    case 3:
                        e_1 = _a.sent();
                        errorMessage = e_1 instanceof Error ? e_1.message : "Unknown initialization error";
                        setInitError(errorMessage);
                        return [3 /*break*/, 5];
                    case 4:
                        setIsInitializing(false);
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        }); };
        initialize();
    }, [documentId, dbData, databases, addDatabase]);
    // The final "ready" state is always derived directly from the context.
    var isReady = isAlreadyInContext;
    return {
        // We are "loading" only if we determined that we should be loading.
        isLoading: shouldLoadDatabase && (isCacheLoading || isInitializing),
        error: cacheError || initError,
        isReady: isReady,
        databaseName: documentId,
    };
}
/**
 * Executes a SQL query against a database managed in the context.
 */
export function useSQLQuery(sql, params, options) {
    var _this = this;
    if (params === void 0) { params = []; }
    if (options === void 0) { options = {}; }
    var _a = options.enabled, enabled = _a === void 0 ? true : _a, databaseName = options.databaseName;
    var databases = useDatabaseContext().databases; // Get the map of all DBs
    var _b = useState([]), data = _b[0], setData = _b[1];
    var _c = useState(false), isLoading = _c[0], setIsLoading = _c[1];
    var _d = useState(null), error = _d[0], setError = _d[1];
    var executeQuery = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var db, stmt, results, errorMessage;
        return __generator(this, function (_a) {
            // We now check for the databaseName and enabled status right at the start.
            if (!enabled || !databaseName) {
                return [2 /*return*/];
            }
            db = databases.get(databaseName);
            // If the database isn't loaded into the context yet, we just wait.
            if (!db) {
                setIsLoading(true); // It's loading, but the DB isn't ready yet.
                return [2 /*return*/];
            }
            setIsLoading(true);
            setError(null);
            try {
                stmt = db.prepare(sql, params);
                results = [];
                while (stmt.step()) {
                    results.push(stmt.getAsObject());
                }
                stmt.free();
                setData(results);
            }
            catch (err) {
                errorMessage = err instanceof Error ? err.message : "Unknown query error";
                setError(errorMessage);
            }
            finally {
                setIsLoading(false);
            }
            return [2 /*return*/];
        });
    }); }, [databaseName, databases, sql, JSON.stringify(params), enabled]);
    useEffect(function () {
        executeQuery();
    }, [executeQuery]);
    return { data: data, isLoading: isLoading, error: error, refetch: executeQuery };
}
